import React, { useState, useCallback, useEffect, useRef, ChangeEvent } from 'react';
import Button from './Button';
import TextInput from './TextInput';
import { cn } from '../lib/utils';
import { Upload, Camera, FileImage, Trash2, AlertCircle } from 'lucide-react';

interface StagedImage {
  id: string;
  file: File;
  base64: string;
  previewUrl: string;
  pageLabel: string;
}

// More permissive type for initial images passed from parent
interface InitialImage {
  id?: string;
  file?: File;
  base64?: string;
  previewUrl?: string;
  pageLabel: string;
}

interface ImageUploadProps {
  onImagesConfirmed: (images: any[]) => void;
  isLoading?: boolean;
  initialImages?: any[];
  title?: string;
}

interface FileErrors {
  [key: string]: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ 
  onImagesConfirmed, 
  isLoading = false, 
  initialImages = [], 
  title = "Upload Essay Pages" 
}) => {
  const [stagedImages, setStagedImages] = useState<StagedImage[]>(() => initialImages);
  const [fileErrors, setFileErrors] = useState<FileErrors>({});

  const isUpdatingFromProps = useRef(false);

  useEffect(() => {
    isUpdatingFromProps.current = true;
    setStagedImages(initialImages);
  }, [initialImages]);

  useEffect(() => {
    if (!isUpdatingFromProps.current) {
      onImagesConfirmed(stagedImages);
    }
    isUpdatingFromProps.current = false;
  }, [stagedImages, onImagesConfirmed]);


  const processFile = useCallback((file: File, existingImageCount: number, batchIndex: number): Promise<StagedImage | null> => {
    return new Promise((resolve) => {
      const timeoutDuration = 10000; // 10 seconds timeout for reading a file
      let timedOut = false;
      let timer: ReturnType<typeof setTimeout> | null = null;

      const finishProcessing = (result: StagedImage | null) => {
        if (timer) clearTimeout(timer);
        resolve(result);
      };

      timer = setTimeout(() => {
        timedOut = true;
        console.error(`Timeout reading file: ${file.name}`);
        setFileErrors(prev => ({ ...prev, [`${file.name}-${Date.now()}-timeout`]: `Timeout processing file: ${file.name}. It might be corrupted or too complex.` }));
        finishProcessing(null);
      }, timeoutDuration);

      if (file.size > 5 * 1024 * 1024) {
        setFileErrors(prev => ({ ...prev, [`${file.name}-${Date.now()}-size`]: "File too large (max 5MB)" }));
        finishProcessing(null);
        return;
      }
      if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
        setFileErrors(prev => ({ ...prev, [`${file.name}-${Date.now()}-type`]: "Invalid file type (accepted: JPEG, PNG, WEBP, GIF)" }));
        finishProcessing(null);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        if (timedOut) return;
        
        try {
            if (typeof reader.result !== 'string') {
              console.error(`Error reading file content for ${file.name}: result is not a string.`);
              setFileErrors(prev => ({ ...prev, [`${file.name}-${Date.now()}-read`]: `Could not read image content for: ${file.name}` }));
              finishProcessing(null);
              return;
            }

            const base64String = reader.result.split(',')[1];
            if (!base64String) {
                console.error(`Error processing base64 data for file: ${file.name}`);
                setFileErrors(prev => ({ ...prev, [`${file.name}-${Date.now()}-base64`]: `Error processing image data for: ${file.name}` }));
                finishProcessing(null);
                return;
            }
            const previewUrl = URL.createObjectURL(file);
            const imageId = `${file.name}-${Date.now()}`;
            
            const nameMatch = file.name.match(/\d+/);
            const defaultLabel = nameMatch ? nameMatch[0] : `${existingImageCount + batchIndex + 1}`;

            finishProcessing({
              id: imageId,
              file,
              base64: base64String,
              previewUrl,
              pageLabel: defaultLabel,
            });
        } catch (e) {
            console.error(`Error in FileReader onloadend for ${file.name}:`, e);
            setFileErrors(prev => ({ ...prev, [`${file.name}-${Date.now()}-onload`]: `Error reading image content for: ${file.name}` }));
            finishProcessing(null);
        }
      };
      reader.onerror = () => {
        if (timedOut) return;
        console.error(`FileReader error for ${file.name}:`, reader.error);
        setFileErrors(prev => ({ ...prev, [`${file.name}-${Date.now()}-read`]: `Error reading file: ${file.name}` }));
        finishProcessing(null);
      }
      
      try {
        reader.readAsDataURL(file);
      } catch (e) {
        console.error(`Error calling readAsDataURL for ${file.name}:`, e);
        setFileErrors(prev => ({ ...prev, [`${file.name}-${Date.now()}-initread`]: `Could not initiate read for file: ${file.name}` }));
        finishProcessing(null);
      }
    });
  }, []); 

  const handleFilesChange = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    console.log("ImageUpload: handleFilesChange triggered. Files:", files);

    if (files && files.length > 0) {
      setFileErrors({}); 
      
      const currentImageCount = stagedImages.length; 
      
      const newImagesPromises = Array.from(files).map((file, index) => processFile(file, currentImageCount, index));
      
      const results = await Promise.allSettled(newImagesPromises);
      
      const successfullyProcessedImages: StagedImage[] = [];
      results.forEach(result => {
          if (result.status === 'fulfilled' && result.value !== null) {
              successfullyProcessedImages.push(result.value);
          } else if (result.status === 'rejected') {
              console.error("ImageUpload: A file processing promise was rejected:", result.reason);
          }
      });

      if (successfullyProcessedImages.length > 0) {
        isUpdatingFromProps.current = false; 
        setStagedImages(prev => {
          const updatedImages = [...prev];
          successfullyProcessedImages.forEach((imgData) => {
            const existingLabels = updatedImages.map(i => i.pageLabel);
            let newLabel = imgData.pageLabel;
            const originalBaseLabel = imgData.pageLabel;
            let counter = 1;
            while (existingLabels.includes(newLabel)) {
              newLabel = `${originalBaseLabel}_v${counter}`;
              counter++;
            }
            updatedImages.push({ ...imgData, pageLabel: newLabel });
          });
          return updatedImages;
        });
      }
    } else {
      console.warn("ImageUpload: No files selected or event.target.files is empty/null.");
      setFileErrors(prev => ({
        ...prev,
        'general-file-error': "No files were selected, or the browser did not provide the files. Please try again."
      }));
    }

    if (event.target) {
      const inputElement = event.target;
      setTimeout(() => {
        if (inputElement) {
          inputElement.value = '';
        }
      }, 0);
    }
  }, [processFile, stagedImages.length]); 

  const handleRemoveImage = useCallback((idToRemove: string) => {
    isUpdatingFromProps.current = false;
    setStagedImages(prev => prev.filter(img => img.id !== idToRemove));
    setFileErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors['general-file-error'];
      return newErrors;
    });
  }, []);

  const handleLabelChange = useCallback((idToUpdate: string, newLabel: string) => {
    isUpdatingFromProps.current = false; 
    setStagedImages(prev => prev.map(img => img.id === idToUpdate ? { ...img, pageLabel: newLabel } : img));
  }, []);


  return (
    <div className="w-full space-y-6">
      <h3 className="text-xl font-semibold text-foreground mb-2">{title}</h3>
      
      {/* Upload Options */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Browse Files Card */}
        <div className="rounded-lg border border-border bg-card p-4 shadow-sm flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Upload className="h-4 w-4 text-primary" />
            </div>
            <p className="text-base font-semibold text-foreground">Upload from computer</p>
          </div>
          <p className="text-sm text-muted-foreground">Choose images already on your device.</p>
          <label
            htmlFor="file-upload-multiple"
            className={cn(
              "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-input bg-background text-foreground font-medium cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors",
              isLoading && "opacity-60 cursor-not-allowed"
            )}
          >
            <FileImage className="h-4 w-4" />
            Browse files
          </label>
          <input
            id="file-upload-multiple"
            type="file"
            multiple
            onChange={handleFilesChange}
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            aria-describedby={Object.keys(fileErrors).length > 0 ? "file-errors-list" : undefined}
            disabled={isLoading}
          />
        </div>

        {/* Camera Card */}
        <div className="rounded-lg border border-border bg-card p-4 shadow-sm flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Camera className="h-4 w-4 text-primary" />
            </div>
            <p className="text-base font-semibold text-foreground">Use camera</p>
          </div>
          <p className="text-sm text-muted-foreground">Snap a page and upload instantly.</p>
          <label
            htmlFor="file-upload-camera"
            className={cn(
              "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-input bg-background text-foreground font-medium cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors",
              isLoading && "opacity-60 cursor-not-allowed"
            )}
          >
            <Camera className="h-4 w-4" />
            Open camera
          </label>
          <input
            id="file-upload-camera"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFilesChange} 
            className="hidden"
            aria-describedby={Object.keys(fileErrors).length > 0 ? "file-errors-list" : undefined}
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Instructions */}
      <p className="text-sm text-muted-foreground">
        Upload images for each page of the essay. Use the input field next to each image to label it with its page number (e.g., "1", "2", "Page 3"). Max file size: 5MB. Accepted: JPEG, PNG, WEBP, GIF.
      </p>

      {/* Error Display */}
      {Object.entries(fileErrors).length > 0 && (
        <div id="file-errors-list" role="alert" className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <h3 className="text-sm font-medium text-destructive">File Errors:</h3>
          </div>
          <ul className="list-disc list-inside text-xs text-destructive/80 space-y-1">
            {Object.entries(fileErrors).map(([key, error]) => {
              const displayKey = key.startsWith('general-file-error') ? "Notice" : key.substring(0, key.lastIndexOf('-'));
              return <li key={key}>{displayKey}: {String(error)}</li>;
            })}
          </ul>
        </div>
      )}

      {/* Staged Images */}
      {stagedImages.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-foreground">Staged Pages ({stagedImages.length})</h3>
          <div className="space-y-3">
            {stagedImages.map((image) => (
              <div key={image.id} className="flex items-center gap-4 p-4 border border-border rounded-lg bg-card shadow-sm">
                <img 
                  src={image.previewUrl} 
                  alt={`Preview for ${image.file.name}`} 
                  className="h-20 w-20 object-contain rounded-lg border border-border bg-secondary/50" 
                />
                <div className="flex-grow min-w-0">
                  <TextInput
                    label="Page Label"
                    id={`label-${image.id}`}
                    value={image.pageLabel}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleLabelChange(image.id, e.target.value)}
                    placeholder="e.g., 1 or Page 1"
                    className="text-sm"
                    disabled={isLoading}
                  />
                  <p className="text-xs text-muted-foreground mt-1 truncate" title={image.file.name}>
                    {image.file.name} ({(image.file.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                </div>
                <Button 
                  onClick={() => handleRemoveImage(image.id)} 
                  variant="destructive" 
                  size="sm" 
                  aria-label={`Remove ${image.file.name}`} 
                  disabled={isLoading}
                  className="gap-1.5"
                >
                  <Trash2 className="h-3 w-3" />
                  Remove
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
